import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import QRCode from 'qrcode';
import connectionService from '../../services/connectionService';

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [retries, setRetries] = useState(0);
  const [lastDisconnect, setLastDisconnect] = useState(null);

  const fetchQRCode = useCallback(async () => {
    if (!whatsAppId) return;
    setLoading(true);
    setError('');
    try {
      const response = await connectionService.getQRCode(whatsAppId);
      const code = typeof response?.qrCode === 'string' ? response.qrCode.trim() : '';
      const nextStatus = response?.status || '';
      setStatus(nextStatus);
      setRetries(Number(response?.retries || 0));
      setLastDisconnect(response?.lastDisconnect || null);
      if (!code) {
        setQrCode('');
        if (String(nextStatus).toUpperCase() === 'CONNECTED') {
          setError('');
        } else {
          setError('QR Code indisponível no backend no momento.');
        }
      } else {
        setQrCode(code);
      }
      return response;
    } catch (err) {
      setError('Erro ao carregar QR Code');
      return null;
    } finally {
      setLoading(false);
    }
  }, [whatsAppId]);

  useEffect(() => {
    if (open && whatsAppId) {
      let stopped = false;
      let timer = null;

      const startAndPoll = async () => {
        const first = await fetchQRCode();
        const firstStatus = String(first?.status || '').toUpperCase();
        const shouldStart =
          !firstStatus ||
          firstStatus === 'DISCONNECTED' ||
          firstStatus === 'PENDING';
        if (shouldStart) {
          try {
            await connectionService.startSession(whatsAppId);
          } catch (_) {}
        }

        const poll = async () => {
          if (stopped) return;
          await fetchQRCode();
          if (stopped) return;
          timer = setTimeout(poll, 2000);
        };

        poll();
      };

      startAndPoll();

      return () => {
        stopped = true;
        if (timer) clearTimeout(timer);
      };
    }
  }, [open, whatsAppId, fetchQRCode]);

  const QRCodeDisplay = ({ value }) => {
    const canvasRef = React.useRef();

    React.useEffect(() => {
      if (typeof value === 'string' && value.length > 0 && canvasRef.current) {
        try {
          QRCode.toCanvas(canvasRef.current, value, {
            width: 256,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });
        } catch (renderErr) {
        }
      }
    }, [value]);

    return <canvas ref={canvasRef} />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>QR Code - Conexão WhatsApp</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" p={2}>
          {(status || retries) && (
            <Typography variant="body2" color="text.secondary" style={{ marginBottom: 12 }}>
              Status: {status || '-'} {retries ? `(tentativas: ${retries})` : ''}
            </Typography>
          )}
          {!!lastDisconnect?.message && status !== 'CONNECTED' && (
            <Typography variant="body2" color="text.secondary" style={{ marginBottom: 12, textAlign: 'center' }}>
              Motivo: {String(lastDisconnect.message)}
            </Typography>
          )}
          {loading && (
            <>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: 16 }}>
                Carregando QR Code...
              </Typography>
            </>
          )}
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
          {qrCode && !loading && (
            <>
              <QRCodeDisplay value={qrCode} />
              <Typography variant="body2" style={{ marginTop: 16, textAlign: 'center' }}>
                Escaneie este QR Code com seu WhatsApp
              </Typography>
            </>
          )}
          {!qrCode && !loading && !error && status === 'CONNECTED' && (
            <Typography variant="body2" color="text.secondary">
              Sessão conectada. Não há QR Code disponível.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={fetchQRCode} color="primary" disabled={loading}>
          Atualizar
        </Button>
        <Button onClick={() => connectionService.restartSession(whatsAppId).then(fetchQRCode).catch(() => {})} color="primary" disabled={loading || !whatsAppId}>
          Gerar novo QR
        </Button>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QrcodeModal;
